import { FormEvent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPatch, apiDelete, ApiError } from "../lib/api";
import { useLibrary } from "../lib/queries";
import { LoadingState, SkeletonList, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";
import { Modal } from "../components/Modal";
import { PencilIcon, TrashIcon, PlayIcon, VideoIcon } from "../components/Icons";
import { getConfigOrNull } from "../config";

/**
 * The tags a brand-new library is offered, not the tags it may have.
 *
 * These five were hardcoded in both the UI and the API with no way for a coach to add one -- somebody
 * else's gym taxonomy, where a skating coach's real filing system is "warm-up", "landing", "edges",
 * "off-season", "rehab — ankle", "the one Ellis can't do yet". As the library grew into the hundreds
 * the borrowed categories stopped being useful exactly when the filter mattered most (UX review F1).
 *
 * They survive as suggestions, which is what they were always good for.
 */
const SUGGESTED_TAGS = ["Strength", "Power", "Plyo", "Core", "Mobility"] as const;
/** Free text now -- a coach's own word for a group of exercises. */
type Tag = string;

interface LibraryEntry {
  id: string;
  name: string;
  tag: Tag;
  description: string | null;
  video_key: string | null;
  video_url: string | null;
}

interface PresignedUpload {
  url: string;
  fields: Record<string, string>;
}

/** A plan still using an exercise the coach tried to delete, as named in the API's 409 body. */
interface BlockingPlan {
  id: string;
  title: string;
  status: string;
  client_name: string | null;
}

function blockingPlansFrom(error: unknown): BlockingPlan[] {
  const plans = error instanceof ApiError ? error.details?.plans : undefined;
  return Array.isArray(plans) ? (plans as BlockingPlan[]) : [];
}

/**
 * Appends only the fields the signed policy authorized -- Content-Type is among them now, supplied by
 * the server rather than added here. S3 rejects a POST carrying any field the policy doesn't mention,
 * so appending an extra Content-Type client-side (as this did previously) failed every upload.
 */
async function uploadVideoFile(upload: PresignedUpload, file: File): Promise<void> {
  const formData = new FormData();
  Object.entries(upload.fields).forEach(([key, value]) => formData.append(key, value));
  formData.append("file", file); // must be appended last -- S3's POST policy expects the file field after all other fields
  const response = await fetch(upload.url, { method: "POST", body: formData });
  if (!response.ok) {
    throw new Error(`Video upload failed (${response.status})`);
  }
}

/** Browsers leave `file.type` empty for some containers; S3 still needs a concrete type to sign against. */
function videoContentType(file: File): string {
  return file.type && file.type.startsWith("video/") ? file.type : "video/mp4";
}

/** 500MB, matching config/environments.ts -- only used if config.json predates the field being published. */
const FALLBACK_MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

function formatMegabytes(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))}MB`;
}

/**
 * Refuses an oversized file before uploading it, rather than after.
 *
 * S3's presigned POST policy has always capped this at 500MB and still does -- that is the enforcement,
 * and it is not moving into the browser. But the only way a coach found out was to upload the entire
 * file over their connection and then get `Video upload failed (400)`, which names neither the size nor
 * the limit. On a phone on rink wifi that is a long wait for a message that doesn't say what went wrong.
 */
function tooLargeMessage(file: File, maxBytes: number): string | null {
  if (file.size <= maxBytes) return null;
  return `That video is ${formatMegabytes(file.size)}. The limit is ${formatMegabytes(maxBytes)} — please trim or compress it and try again.`;
}

/**
 * Inline edit for one entry.
 *
 * Its own component so the fields start from the entry being edited rather than from shared page state.
 * Only one row is ever open, so switching rows unmounts this and mounts a fresh one -- which is what
 * stops a half-finished edit on one exercise from appearing in another's fields.
 */
function EditEntryForm({
  entry,
  pending,
  onCancel,
  onSave,
}: {
  entry: LibraryEntry;
  pending: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState(entry.name);
  const [tag, setTag] = useState<Tag>(entry.tag);
  const [description, setDescription] = useState(entry.description ?? "");

  return (
    <form
      className="plan-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim()) return;
        onSave({ name: name.trim(), tag, description: description.trim() || null });
      }}
    >
      <label htmlFor={`edit-name-${entry.id}`}>Exercise name</label>
      <input id={`edit-name-${entry.id}`} value={name} onChange={(e) => setName(e.target.value)} required />

      <label htmlFor={`edit-tag-${entry.id}`}>Tag</label>
      {/* A text box with suggestions, not a closed list. `list` gives the coach everything they have
          already used plus the starting five, while still letting them type a new one (F1). */}
      <input
        id={`edit-tag-${entry.id}`}
        list="library-tag-suggestions"
        value={tag}
        onChange={(e) => setTag(e.target.value)}
        required
      />

      <label htmlFor={`edit-description-${entry.id}`}>Description / coaching cues</label>
      <textarea
        id={`edit-description-${entry.id}`}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <div className="card-actions">
        <button type="submit" disabled={pending}>
          {pending ? "Saving…" : "Save changes"}
        </button>
        <button type="button" className="chip" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Coach-facing exercise library: create entries, upload/preview video, search + tag-filter (PRD §6.5). */
export function Library() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState<Tag | "all">("all");

  const [name, setName] = useState("");
  const [tag, setTag] = useState<Tag>("Strength");
  const [description, setDescription] = useState("");
  /**
   * The file chosen on the create form, so a video can be attached while adding rather than only to an
   * entry that already exists. Held as state rather than read off the form at submit time so the
   * chosen filename can be shown back, and so it can be cleared after a successful add.
   */
  const [newVideo, setNewVideo] = useState<File | null>(null);
  const [videoErrors, setVideoErrors] = useState<Record<string, boolean>>({});
  /** Set when a chosen file is refused for its size, before any upload is attempted. */
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  /**
   * The whole entry rather than its id, for each dialog.
   *
   * A dialog needs the entry's fields to render, and holding only an id meant looking it back up in a
   * list that react-query can replace underneath it -- at which point the dialog is showing one
   * exercise's title over another's data, or nothing at all.
   */
  const [editingEntry, setEditingEntry] = useState<LibraryEntry | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<LibraryEntry | null>(null);
  const [previewEntry, setPreviewEntry] = useState<LibraryEntry | null>(null);

  const entriesQuery = useLibrary<LibraryEntry[]>(search, tagFilter);
  /** Whether the coach narrowed the list -- which decides which of the two empty states applies. */
  const isFiltered = search.trim() !== "" || tagFilter !== "all";
  /**
   * Every tag this coach has actually used, for the filter chips and the create/edit suggestions.
   *
   * Derived from the entries rather than stored: a tag exists precisely because something carries it,
   * so there is no separate list to keep in step, and one that emptied out would leave chips filtering
   * to nothing. The currently-selected tag is folded in so the chip you are filtering by cannot vanish
   * from under you when it matches nothing.
   */
  const tagsInUse = Array.from(
    new Set([
      ...(entriesQuery.data ?? []).map((entry) => entry.tag).filter(Boolean),
      ...(tagFilter !== "all" ? [tagFilter] : []),
    ])
  ).sort((a, b) => a.localeCompare(b));

  const maxUploadBytes = getConfigOrNull()?.maxUploadContentLengthBytes ?? FALLBACK_MAX_UPLOAD_BYTES;

  /**
   * Gate every chosen file through one place, so the create form and the per-row Replace control can't
   * disagree about the limit. Returns the file when it's acceptable, null when it was refused.
   */
  function acceptFile(file: File | undefined | null): File | null {
    if (!file) return null;
    const problem = tooLargeMessage(file, maxUploadBytes);
    setFileSizeError(problem);
    return problem ? null : file;
  }

  function invalidateLibrary() {
    return queryClient.invalidateQueries({ queryKey: ["library"] });
  }

  /**
   * The three-call upload dance, in one place.
   *
   * The server cannot take the bytes with the entry: the browser uploads straight to S3 under a
   * presigned policy scoped to `videos/<entryId>`, so the entry has to exist before there is a key to
   * sign for, and S3 never calls back, which is what confirm-upload is for. None of that is the coach's
   * problem, so both the create form and the per-row control drive this same function.
   */
  async function attachVideo(entryId: string, file: File): Promise<void> {
    const upload = await apiPost<PresignedUpload>(`/library/${entryId}/upload-url`, {
      contentType: videoContentType(file),
    });
    await uploadVideoFile(upload, file);
    await apiPost(`/library/${entryId}/confirm-upload`);
  }

  const createEntry = useMutation({
    mutationFn: async (input: { body: Record<string, unknown>; file: File | null }) => {
      const entry = await apiPost<LibraryEntry>("/library", input.body);
      if (input.file) {
        /**
         * A failed upload must not discard the entry the coach just typed out. The entry is already
         * saved by this point, and re-creating it is not something they can do without retyping -- so
         * this reports the video failure against an entry that exists and can be retried from its row.
         */
        try {
          await attachVideo(entry.id, input.file);
        } catch {
          throw new Error(`"${entry.name}" was added, but its video didn't upload. Use Replace video to try again.`);
        }
      }
      return entry;
    },
    onSuccess: async () => {
      setName("");
      setDescription("");
      setNewVideo(null);
      await invalidateLibrary();
    },
    // The entry may exist even on failure (video-only failure above), so the list is refetched either
    // way -- otherwise a successful add with a failed upload would not appear until a manual reload.
    onError: () => invalidateLibrary(),
  });

  const uploadVideo = useMutation({
    mutationFn: async ({ entryId, file }: { entryId: string; file: File }) => {
      await attachVideo(entryId, file);
      return entryId;
    },
    onSuccess: async (entryId) => {
      setVideoErrors((prev) => ({ ...prev, [entryId]: false }));
      await invalidateLibrary();
    },
  });

  const editEntry = useMutation({
    mutationFn: ({ entryId, body }: { entryId: string; body: Record<string, unknown> }) =>
      apiPatch(`/library/${entryId}`, body),
    onSuccess: async () => {
      setEditingEntry(null);
      await invalidateLibrary();
    },
  });

  const deleteEntry = useMutation({
    mutationFn: (entryId: string) => apiDelete(`/library/${entryId}`),
    onSuccess: async () => {
      setConfirmingDelete(null);
      await invalidateLibrary();
    },
  });

  function handleCreate(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    createEntry.mutate({
      body: { name: name.trim(), tag, description: description.trim() || null },
      file: newVideo,
    });
  }

  const entries = entriesQuery.data;
  // The delete refusal is rendered against its own row rather than here, so it sits next to the thing
  // it refers to instead of at the top of the page away from the button that produced it.
  // Only the errors from actions taken on the page itself. createEntry and editEntry happen inside
  // modals, so their errors render inside those modals rather than behind them.
  const actionError = uploadVideo.error;
  const blockingPlans = blockingPlansFrom(deleteEntry.error);

  return (
    // The nav bar's Clients tab is the way back now -- the library is a top-level destination, not a
    // drill-down, so it doesn't need a back link of its own.
    <PageShell
      title="Exercise library"
      actions={
        <button type="button" onClick={() => setShowAddForm(true)}>
          Add exercise
        </button>
      }
    >
      {actionError && <ErrorState error={actionError} />}
      {/* Not routed through ErrorState: nothing failed and there is nothing to retry -- the file was
          refused before anything was attempted, and the fix is to pick a different one. */}
      {fileSizeError && (
        <p role="alert" className="error">
          {fileSizeError}
        </p>
      )}
      {entriesQuery.isError && <ErrorState error={entriesQuery.error} onRetry={() => entriesQuery.refetch()} />}

      {/* In a dialog rather than permanently on the page: adding is occasional, browsing is constant,
          and a tall always-open form pushed the exercises themselves below the fold. */}
      {showAddForm && (
        <Modal title="Add exercise" onClose={() => setShowAddForm(false)}>
          <form className="plan-form" onSubmit={handleCreate}>
            <label htmlFor="entry-name">Exercise name</label>
            <input id="entry-name" value={name} onChange={(e) => setName(e.target.value)} required />

            <label htmlFor="entry-tag">Tag</label>
            <input
              id="entry-tag"
              list="library-tag-suggestions"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="e.g. Landing, Edges, Rehab — ankle"
              required
            />

            <label htmlFor="entry-description">Description / coaching cues</label>
            <textarea id="entry-description" value={description} onChange={(e) => setDescription(e.target.value)} />

            <label htmlFor="entry-video">Video (optional)</label>
            <input
              id="entry-video"
              type="file"
              accept="video/*"
              onChange={(e) => setNewVideo(acceptFile(e.target.files?.[0]))}
            />

            {createEntry.isError && <ErrorState error={createEntry.error} />}
            <button type="submit" disabled={createEntry.isPending}>
              {createEntry.isPending ? (newVideo ? "Adding and uploading…" : "Adding…") : "Add to library"}
            </button>
          </form>
        </Modal>
      )}

      {/* One suggestion list, shared by the create and edit dialogs: everything this coach has already
          used, plus the starting five for a library that has none yet (F1). */}
      <datalist id="library-tag-suggestions">
        {Array.from(new Set([...tagsInUse, ...SUGGESTED_TAGS])).map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search library"
        />
        <div className="filter-chips" role="group" aria-label="Filter by tag">
          <button className={tagFilter === "all" ? "chip active" : "chip"} onClick={() => setTagFilter("all")}>
            All
          </button>
          {/* Built from the tags actually in use, not from a fixed list -- which is the whole point of
              letting a coach name their own. Sorted so the row doesn't reshuffle as entries load. */}
          {tagsInUse.map((t) => (
            <button key={t} className={tagFilter === t ? "chip active" : "chip"} onClick={() => setTagFilter(t)}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {entriesQuery.isPending && (
        <>
          <LoadingState label="Loading the library…" />
          <SkeletonList />
        </>
      )}

      {/*
        Two different empty states, because they need to say opposite things.

        The first-run one was being used for both, so a coach who had just spent an evening building a
        library, then clicked the "Power" chip with nothing tagged Power, was told his library was empty
        (UX review F82). "Add your first exercise" and "nothing matched, clear the filter" are not the
        same message, and the second needs to name what was filtered and offer the way back.
      */}
      {entriesQuery.isSuccess && entries && entries.length === 0 && (
        isFiltered ? (
          <div className="empty-state">
            <p>
              Nothing here matches{" "}
              {search.trim() && <strong>&ldquo;{search.trim()}&rdquo;</strong>}
              {search.trim() && tagFilter !== "all" && " in "}
              {tagFilter !== "all" && <strong>{tagFilter}</strong>}.
            </p>
            <button
              type="button"
              className="chip"
              onClick={() => {
                setSearch("");
                setTagFilter("all");
              }}
            >
              Show every exercise
            </button>
          </div>
        ) : (
          <p className="empty-state">No exercises in the library yet.</p>
        )
      )}

      {/*
        Compact rows rather than tall cards. The library is expected to grow into the hundreds, and the
        previous layout gave every exercise an inline video player and a stack of text buttons -- so a
        handful filled the screen and scanning for one was slow. Now: a thumbnail that opens a preview,
        the name and cue, its tag, and the actions as icons.
      */}
      <ul className="library-list">
        {entries?.map((entry) => (
          <li key={entry.id} className="library-row">
            <button
              type="button"
              /* `no-video` is a visible distinction, not just an accessible one. Both states rendered as
                 the same grey box, so the list gave no signal about which exercises actually have a
                 walkthrough -- which is exactly what a coach is scanning for while building a plan
                 (UX review F83). */
              className={entry.video_url ? "library-thumb" : "library-thumb no-video"}
              disabled={!entry.video_url}
              onClick={() => setPreviewEntry(entry)}
              aria-label={entry.video_url ? `Play the video for ${entry.name}` : `${entry.name} has no video`}
            >
              {/* The real first frame, not a generic play icon (finding #19). A coach who uploaded the
                  wrong clip could not tell from a row of identical triangles; seeing the frame is how
                  you catch it. preload="metadata" fetches only enough to render that frame, so a
                  library of thirty exercises does not pull thirty videos.

                  No server-side thumbnail generation: that would mean a transcode step, somewhere to
                  put the output, and a second thing to keep in step with the video it came from. */}
              {entry.video_url ? (
                <>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <video className="library-thumb-video" src={entry.video_url} preload="metadata" muted playsInline />
                  <span className="library-thumb-play">
                    <PlayIcon />
                  </span>
                </>
              ) : (
                <VideoIcon />
              )}
            </button>

            <div className="library-row-main">
              <span className="library-row-name">{entry.name}</span>
              {entry.description && <span className="library-row-desc">{entry.description}</span>}
              {uploadVideo.isPending && uploadVideo.variables?.entryId === entry.id && (
                <span className="library-row-desc">Uploading…</span>
              )}
            </div>

            <span className="badge">{entry.tag}</span>

            <div className="library-row-actions">
              {/* A file input styled as an icon button. The label IS the control, so the hidden input
                  keeps its accessible name from the text inside rather than needing a separate id. */}
              <label className="icon-button" title={entry.video_key ? "Replace video" : "Upload video"}>
                <VideoIcon />
                <span className="visually-hidden">
                  {entry.video_key ? `Replace the video for ${entry.name}` : `Upload a video for ${entry.name}`}
                </span>
                <input
                  type="file"
                  accept="video/*"
                  disabled={uploadVideo.isPending}
                  onChange={(e) => {
                    const file = acceptFile(e.target.files?.[0]);
                    if (file) uploadVideo.mutate({ entryId: entry.id, file });
                  }}
                />
              </label>
              <button type="button" className="icon-button" onClick={() => setEditingEntry(entry)} title="Edit">
                <PencilIcon />
                <span className="visually-hidden">Edit {entry.name}</span>
              </button>
              {/* Set apart from the other two. Delete sat immediately beside Edit at roughly a 13px
                  target, so on a phone the destructive action was a mis-tap away from the one Sam
                  actually wanted, with only the confirmation between him and losing an exercise's cues
                  (UX review F83). The targets are thumb-sized now and this one is separated. */}
              <button
                type="button"
                className="icon-button danger set-apart"
                onClick={() => {
                  deleteEntry.reset();
                  setConfirmingDelete(entry);
                }}
                title="Delete"
              >
                <TrashIcon />
                <span className="visually-hidden">Delete {entry.name}</span>
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* Edit in a dialog: an inline form re-flowed the list around whichever row was open, moving every
          other exercise on the page while the coach was looking at it. */}
      {editingEntry && (
        <Modal title={`Edit ${editingEntry.name}`} onClose={() => setEditingEntry(null)}>
          {editEntry.isError && <ErrorState error={editEntry.error} />}
          <EditEntryForm
            entry={editingEntry}
            pending={editEntry.isPending}
            onCancel={() => setEditingEntry(null)}
            onSave={(body) => editEntry.mutate({ entryId: editingEntry.id, body })}
          />
        </Modal>
      )}

      {confirmingDelete && (
        <Modal title="Delete exercise" onClose={() => setConfirmingDelete(null)}>
          <p>Delete “{confirmingDelete.name}”? This also removes its video, and cannot be undone.</p>
          {deleteEntry.isError && (
            <div className="error">
              <p>{deleteEntry.error.message}</p>
              {/* Naming the plans matters: without them the coach has to open every plan to find where
                  the exercise is still used. */}
              {blockingPlans.length > 0 && (
                <ul>
                  {blockingPlans.map((plan) => (
                    <li key={plan.id}>
                      {plan.title}
                      {plan.client_name ? ` — ${plan.client_name}` : ""} ({plan.status})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="card-actions">
            <button
              type="button"
              className="chip danger"
              disabled={deleteEntry.isPending}
              onClick={() => deleteEntry.mutate(confirmingDelete.id)}
            >
              {deleteEntry.isPending ? "Deleting…" : "Yes, delete"}
            </button>
            <button type="button" className="chip" onClick={() => setConfirmingDelete(null)}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* One player, opened on demand, rather than an element per row all holding signed URLs open. */}
      {previewEntry?.video_url && (
        <Modal title={previewEntry.name} onClose={() => setPreviewEntry(null)}>
          {videoErrors[previewEntry.id] ? (
            <p className="error">This video couldn't be loaded. Try uploading it again.</p>
          ) : (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              controls
              autoPlay
              src={previewEntry.video_url}
              className="library-video"
              // Without this the player renders blank when the signed URL 403s or the object isn't
              // really there -- the coach sees "a video" that silently never plays.
              onError={() => setVideoErrors((prev) => ({ ...prev, [previewEntry.id]: true }))}
            />
          )}
        </Modal>
      )}
    </PageShell>
  );
}
